import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { orderService, authApi } from '../services/api';
import type { Order, SavedAddress, SubscriptionState } from '../types';
import { formatCentsUSD } from '../lib/money';
import { Package, MapPin, LogOut, Truck, Clock, Loader2, Mail } from 'lucide-react';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  paid: { label: 'Paid', color: 'bg-blue-100 text-blue-800' },
  shipped: { label: 'Shipped', color: 'bg-purple-100 text-purple-800' },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-800' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800' },
  refunded: { label: 'Refunded', color: 'bg-gray-200 text-gray-800' },
};

function SignInForm() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="py-16">
      <div className="container-max max-w-md mx-auto">
        <h1 className="text-3xl font-serif font-bold text-coastal-600 mb-2 text-center">
          {mode === 'login' ? 'Sign In' : 'Create Account'}
        </h1>
        <p className="text-gray-600 text-center mb-8">
          {mode === 'login'
            ? 'Sign in to view your orders and saved addresses.'
            : 'Create an account to track orders and save shipping info.'}
        </p>

        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  minLength={8}
                  required
                />
              </div>
              {mode === 'login' && (
                <div className="text-right -mt-2">
                  <Link to="/forgot-password" className="text-sm text-coastal-600 font-medium hover:underline">
                    Forgot password?
                  </Link>
                </div>
              )}
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
              </Button>
            </form>
            <div className="mt-4 text-center text-sm text-gray-600">
              {mode === 'login' ? (
                <>
                  Don&apos;t have an account?{' '}
                  <button
                    type="button"
                    onClick={() => { setMode('register'); setError(''); }}
                    className="text-coastal-600 font-medium hover:underline"
                  >
                    Create one
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => { setMode('login'); setError(''); }}
                    className="text-coastal-600 font-medium hover:underline"
                  >
                    Sign in
                  </button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function AccountPage() {
  const { user, token, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [addresses, setAddresses] = useState<SavedAddress[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [tab, setTab] = useState<'orders' | 'addresses' | 'newsletter'>('orders');

  useEffect(() => {
    if (!token) {
      setOrdersLoading(false);
      setAddressesLoading(false);
      return;
    }
    setOrdersLoading(true);
    setAddressesLoading(true);
    orderService.getHistory(token)
      .then(setOrders)
      .catch(console.error)
      .finally(() => setOrdersLoading(false));
    authApi.getAddresses(token)
      .then(setAddresses)
      .catch(console.error)
      .finally(() => setAddressesLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="py-16 text-center">
        <Loader2 className="w-8 h-8 text-coastal-600 animate-spin mx-auto mb-3" />
        <p className="text-gray-500">Loading your account...</p>
      </div>
    );
  }

  if (!user) {
    return <SignInForm />;
  }

  function handleLogout() {
    logout();
    navigate('/');
  }

  async function handleDeleteAddress(id: string) {
    if (!token) return;
    await authApi.deleteAddress(token, id);
    setAddresses(prev => prev.filter(a => a.id !== id));
  }

  return (
    <div>
      <section className="bg-cream py-8">
        <div className="container-max flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-serif font-bold text-coastal-600">My Account</h1>
            <p className="text-gray-600">{user.email}</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout} className="flex items-center gap-2">
            <LogOut className="w-4 h-4" /> Sign Out
          </Button>
        </div>
      </section>

      <section className="py-8">
        <div className="container-max">
          <div className="flex gap-4 mb-8 border-b">
            <button onClick={() => setTab('orders')}
              className={`pb-3 px-1 font-medium transition-colors ${tab === 'orders' ? 'border-b-2 border-coastal-600 text-coastal-600' : 'text-gray-500 hover:text-gray-700'}`}>
              <Package className="w-4 h-4 inline mr-2" />Order History
            </button>
            <button onClick={() => setTab('addresses')}
              className={`pb-3 px-1 font-medium transition-colors ${tab === 'addresses' ? 'border-b-2 border-coastal-600 text-coastal-600' : 'text-gray-500 hover:text-gray-700'}`}>
              <MapPin className="w-4 h-4 inline mr-2" />Saved Addresses
            </button>
            <button onClick={() => setTab('newsletter')}
              className={`pb-3 px-1 font-medium transition-colors ${tab === 'newsletter' ? 'border-b-2 border-coastal-600 text-coastal-600' : 'text-gray-500 hover:text-gray-700'}`}>
              <Mail className="w-4 h-4 inline mr-2" />Newsletter
            </button>
          </div>

          {tab === 'orders' && (
            <div>
              {ordersLoading ? (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 text-coastal-600 animate-spin mx-auto mb-3" />
                  <p className="text-gray-500">Loading your orders...</p>
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No orders yet</p>
                  <Link to="/products"><Button>Start Shopping</Button></Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map(order => {
                    const st = STATUS_LABELS[order.status] || STATUS_LABELS.pending;
                    return (
                      <Card key={order.id}>
                        <CardContent className="pt-6">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                            <div>
                              <p className="font-mono text-sm text-gray-500">{order.id}</p>
                              <p className="text-sm text-gray-500">{new Date(order.createdAt).toLocaleDateString()}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${st.color}`}>
                                {st.label}
                              </span>
                              <span className="font-bold text-lg">{formatCentsUSD(order.totalCentsUSD)}</span>
                            </div>
                          </div>

                          <div className="text-sm text-gray-600 mb-3">
                            {order.items.length} item{order.items.length !== 1 ? 's' : ''} &middot;
                            Shipped to {order.shipping.city}, {order.shipping.state} {order.shipping.zip}
                          </div>

                          {order.refundedCentsUSD > 0 ? (
                            <div className="bg-gray-100 rounded-lg p-3 flex items-center gap-3 text-sm">
                              <Package className="w-5 h-5 text-gray-600 flex-shrink-0" />
                              <p className="text-gray-700">
                                {order.status === 'refunded' ? 'Fully refunded' : 'Partially refunded'}.{' '}
                                {formatCentsUSD(order.refundedCentsUSD)} returned to your payment method
                              </p>
                            </div>
                          ) : order.tracking ? (
                            <div className="bg-purple-50 rounded-lg p-3 flex items-center gap-3 text-sm">
                              <Truck className="w-5 h-5 text-purple-600 flex-shrink-0" />
                              <div>
                                <p className="font-medium text-purple-800">
                                  {order.tracking.carrier}: {order.tracking.number}
                                </p>
                                <p className="text-purple-600">Estimated delivery within 3-5 business days</p>
                              </div>
                            </div>
                          ) : order.status === 'paid' ? (
                            <div className="bg-blue-50 rounded-lg p-3 flex items-center gap-3 text-sm">
                              <Clock className="w-5 h-5 text-blue-600 flex-shrink-0" />
                              <p className="text-blue-800">Being prepared for shipment</p>
                            </div>
                          ) : null}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === 'addresses' && (
            <div>
              {addressesLoading ? (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 text-coastal-600 animate-spin mx-auto mb-3" />
                  <p className="text-gray-500">Loading addresses...</p>
                </div>
              ) : addresses.length === 0 ? (
                <div className="text-center py-12">
                  <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No saved addresses. Your shipping address will be saved automatically at checkout.</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {addresses.map(addr => (
                    <Card key={addr.id}>
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div>
                            {addr.isDefault && (
                              <span className="inline-block bg-coastal-100 text-coastal-800 text-xs px-2 py-0.5 rounded mb-2">Default</span>
                            )}
                            <p className="font-medium">{addr.name}</p>
                            <p className="text-sm text-gray-600">{addr.line1}</p>
                            {addr.line2 && <p className="text-sm text-gray-600">{addr.line2}</p>}
                            <p className="text-sm text-gray-600">{addr.city}, {addr.state} {addr.zip}</p>
                          </div>
                          <button onClick={() => handleDeleteAddress(addr.id)}
                            className="text-red-500 hover:text-red-700 text-sm">Remove</button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'newsletter' && <NewsletterPreferences token={token} initial={user.subscription} />}
        </div>
      </section>
    </div>
  );
}

function NewsletterPreferences({ token, initial }: { token: string | null; initial?: SubscriptionState }) {
  const [state, setState] = useState<SubscriptionState | null>(initial ?? null);
  const [loading, setLoading] = useState(!initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token || initial) return;
    authApi.getSubscription(token)
      .then(setState)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token, initial]);

  async function handleToggle(subscribe: boolean) {
    if (!token) return;
    setSaving(true);
    setError('');
    try {
      const next = await authApi.updateSubscription(token, subscribe);
      setState(next);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not update your preference.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="w-8 h-8 text-coastal-600 animate-spin mx-auto mb-3" />
        <p className="text-gray-500">Loading your preferences...</p>
      </div>
    );
  }

  const subscribed = state?.subscribed ?? false;

  return (
    <div className="max-w-xl">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <Mail className="w-8 h-8 text-coastal-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <p className="font-medium text-gray-900">Email updates</p>
              <p className="text-sm text-gray-600 mb-4">
                {subscribed
                  ? "You're subscribed! We'll email you about new harvests and new locations."
                  : 'Get notified about new harvests and when our products arrive at a location near you.'}
              </p>
              {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
              {subscribed ? (
                <Button variant="outline" size="sm" onClick={() => handleToggle(false)} disabled={saving}>
                  {saving ? 'Saving…' : 'Unsubscribe'}
                </Button>
              ) : (
                <Button size="sm" onClick={() => handleToggle(true)} disabled={saving}>
                  {saving ? 'Saving…' : 'Subscribe'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
